/**
 * PrivateSettlementList - 私账往来列表组件
 * 
 * 显示私账模式下成员之间的往来结算关系
 */
import React from 'react';
import { View, Text, StyleSheet, ViewStyle, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import PlatformIcon from '../common/PlatformIcon';
import Card from '../common/Card';
import Avatar from '../common/Avatar';
import { MODULE_COLORS } from '@cubemeet/core';

export interface PrivateSettlement {
  /**
   * 支付方用户ID
   */
  fromUserId: string;
  /**
   * 支付方显示名称
   */
  fromName: string;
  /**
   * 支付方头像URL
   */
  fromAvatarUrl: string | null;
  /**
   * 接收方用户ID
   */
  toUserId: string;
  /**
   * 接收方显示名称
   */
  toName: string;
  /**
   * 接收方头像URL
   */
  toAvatarUrl: string | null;
  /**
   * 结算金额
   */
  amount: number;
}

export interface PrivateSettlementListProps {
  /**
   * 结算数据列表
   */
  settlements: PrivateSettlement[];
  /**
   * 当前用户ID（用于判断是否高亮显示）
   */
  currentUserId?: string | null;
  /**
   * 货币代码
   */
  currency?: string;
  /**
   * 是否有其他人的私账记录（影响空状态显示）
   */
  hasOtherRecords?: boolean;
  /**
   * 格式化货币显示
   */
  formatCurrency: (amount: number, currency?: string) => string;
  /**
   * 翻译函数
   */
  t?: (key: string, params?: Record<string, any>) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
}

const defaultT = (key: string, params?: Record<string, any>) => {
  const translations: Record<string, string> = {
    'privateSettlement.title': '私账往来',
    'privateSettlement.amount': '{payer} 应付 {receiver} {amount}',
    'privateSettlement.youOwe': '你欠 {receiver} {amount}',
    'privateSettlement.owesYou': '{payer} 欠你 {amount}',
    'privateSettlement.empty': '当前没有成员之间的往来记录',
    'privateSettlement.noPersonalSettlements': '你没有任何私人往来账',
    'privateSettlement.you': '你',
  };
  let text = translations[key] || key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, String(v));
    });
  }
  return text;
};

/**
 * PrivateSettlementList 组件
 */
const PrivateSettlementList: React.FC<PrivateSettlementListProps> = ({
  settlements,
  currentUserId,
  currency,
  hasOtherRecords = false,
  formatCurrency,
  t = defaultT,
  style,
}) => {
  const youLabel = t('privateSettlement.you');

  // 空状态
  if (settlements.length === 0) {
    return (
      <View style={[styles.container, style]}>
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <View style={styles.headerIcon}>
              <Icon name="receipt" size={20} color={MODULE_COLORS.travel} />
            </View>
            <Text style={styles.title}>{t('privateSettlement.title')}</Text>
          </View>
        </View>
        <View style={styles.emptyState}>
          <Icon name="handshake" size={42} color={MODULE_COLORS.travel} style={styles.emptyIcon} />
          <Text style={styles.emptyText}>
            {hasOtherRecords
              ? t('privateSettlement.noPersonalSettlements')
              : t('privateSettlement.empty')}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <View style={styles.headerIcon}>
            <Icon name="receipt" size={20} color={MODULE_COLORS.travel} />
          </View>
          <Text style={styles.title}>{t('privateSettlement.title')}</Text>
        </View>
      </View>

      {settlements.map((settlement) => {
        const fromLabel =
          settlement.fromUserId && currentUserId === settlement.fromUserId
            ? youLabel
            : settlement.fromName;
        const toLabel =
          settlement.toUserId && currentUserId === settlement.toUserId
            ? youLabel
            : settlement.toName;
        const amountText = formatCurrency(settlement.amount, currency);

        // 根据当前用户位置生成不同的结算文本
        let settlementLine = t('privateSettlement.amount', {
          payer: fromLabel,
          receiver: toLabel,
          amount: amountText,
        });

        if (currentUserId) {
          if (settlement.fromUserId === currentUserId) {
            settlementLine = t('privateSettlement.youOwe', {
              receiver: toLabel,
              amount: amountText,
            });
          } else if (settlement.toUserId === currentUserId) {
            settlementLine = t('privateSettlement.owesYou', {
              payer: fromLabel,
              amount: amountText,
            });
          }
        }

        return (
          <View key={`${settlement.fromUserId}-${settlement.toUserId}`} style={styles.settlementItem}>
            <Card style={styles.innerCard}>
              <View style={styles.settlementRow}>
                {/* 支付方 */}
                <View style={[styles.participant, styles.participantLeft]}>
                  <Avatar
                    avatarUrl={settlement.fromAvatarUrl}
                    displayName={fromLabel}
                    size="small"
                    style={styles.avatar}
                  />
                  <Text
                    style={[
                      styles.participantName,
                      settlement.fromUserId === currentUserId && styles.participantNameSelf,
                    ]}
                  >
                    {fromLabel}
                  </Text>
                </View>

                {/* 箭头容器 - 固定宽度，垂直居中，使线和箭头在同一水平线 */}
                <View style={styles.arrowContainerFixed}>
                  <View style={styles.arrowPill}>
                    <PlatformIcon name="chevron-right" size={18} color="#64748b" />
                  </View>
                </View>

                {/* 接收方 */}
                <View style={[styles.participant, styles.participantRight]}>
                  <Avatar
                    avatarUrl={settlement.toAvatarUrl}
                    displayName={toLabel}
                    size="small"
                    style={styles.avatar}
                  />
                  <Text
                    style={[
                      styles.participantName,
                      settlement.toUserId === currentUserId && styles.participantNameSelf,
                    ]}
                  >
                    {toLabel}
                  </Text>
                </View>
              </View>

              {/* 结算金额描述 */}
              <Text style={styles.settlementAmount}>{settlementLine}</Text>
            </Card>
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 0,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingTop: 4,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIcon: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  settlementItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  settlementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  innerCard: {
    paddingVertical: 18,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  participant: {
    flex: 1,
    alignItems: 'center',
  },
  participantLeft: {
    alignItems: 'flex-start',
  },
  participantRight: {
    alignItems: 'flex-end',
  },
  avatar: {
    marginBottom: 6,
  },
  participantName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  participantNameSelf: {
    color: MODULE_COLORS.travel,
  },
  arrowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 12,
  },
  arrowContainerFixed: {
    width: 72,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowLine: {
    width: 48,
    height: 2,
    backgroundColor: '#cbd5e1',
    marginRight: 8,
  },
  arrowPill: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#f8fafc',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e6eef9',
  },
  settlementAmount: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyIcon: {
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 14,
    color: '#9ca3af',
    textAlign: 'center',
  },
});

export default PrivateSettlementList;
